/**
 * Done. CPD — OnboardingLayout (Stitch v2)
 *
 * Wide editorial shell that hosts the personalisation survey. Matches the
 * public landing page's Stitch v2 token set: warm clinical background,
 * Archivo Narrow brand mark, deep petrol teal accents, PublicFooter.
 * Progress is rendered inside SurveyOverview, so the layout no longer
 * renders progress dots.
 */

import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { PublicFooter } from '@/components/layout/PublicFooter';

interface OnboardingLayoutProps {
  children: ReactNode;
}

export function OnboardingLayout({ children }: OnboardingLayoutProps): React.ReactElement {
  return (
    <div className="flex min-h-screen flex-col bg-[var(--stitch-background)] text-[var(--stitch-on-background)] stitch-font-body">
      <header
        className="sticky top-0 z-40 h-20 bg-[var(--stitch-surface)]/85 backdrop-blur-md border-b border-[var(--stitch-surface-container-highest)]"
        data-print-hidden
      >
        <div
          className="h-full mx-auto flex items-center justify-between px-4 md:px-10"
          style={{ maxWidth: 'var(--stitch-container-max)' }}
        >
          <Link
            href="/"
            className="flex items-center gap-2 active:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--stitch-primary)] rounded"
          >
            <span className="material-symbols-outlined text-[28px] text-[var(--stitch-primary)]">clinical_notes</span>
            <span className="stitch-font-display text-[24px] md:text-[28px] font-bold tracking-tighter text-[var(--stitch-primary)] uppercase">
              Done. CPD
            </span>
          </Link>
          <span className="stitch-font-label text-[12px] tracking-widest uppercase text-[var(--stitch-on-surface-variant)] hidden sm:inline">
            Personalisation
          </span>
        </div>
      </header>

      <main
        className="flex-1 w-full mx-auto px-4 md:px-10 pt-10 pb-20 md:pt-16"
        style={{ maxWidth: 'var(--stitch-container-max)' }}
        aria-label="Onboarding survey"
      >
        <div className="mx-auto w-full max-w-4xl">
          {children}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
